import React, { useState, useRef, useEffect } from "react";
import { IonIcon, IonRippleEffect } from "@ionic/react";
import {
  chevronForward,
  logOutOutline,
  personCircleOutline,
  swapHorizontalOutline,
} from "ionicons/icons";
import "./SideNav.css";
import logo from "../../assets/icons/arc-logo-with-text.svg";
import avatarsSprite from "../../assets/icons/avatars.svg";
import useAuth from "../../hooks/useAuth";
import { RequestPopup } from "../layout/Guest/Components/RequestPopup";
import { getUserName } from "../../utils/helpers";
import Avatar from "../ui/Avatar";
import InitialsAvatar from "../ui/InitialsAvatar";
import { getInitials } from "../../utils/helpers";
import { isCustomAvatar } from "../../pages/settings/AvatarSelection";
import {
  goToProviderPortal,
  portalSwitchUrl,
  SWITCH_TO_PORTAL_LABEL,
} from "../../services/staffPortal";

interface SideNavProps {
  activeTab: string;
  tabs: Array<{
    path: string;
    name: string;
    icon: any;
    showInTabs: boolean;
    invert?: boolean;
  }>;
  onNavigate: (tabName: string) => void; // Pass tab name (matches BottomNav)
  onLongPressStart: (onLongPress: () => void) => void;
  onLongPressEnd: () => void;
  onTabLongPress: (tabName: string) => void;
  /** Slide off-screen instead of unmounting, so it can animate. */
  collapsed?: boolean;
}

const iconCache = new Map<string, React.ComponentType<any>>();

const SideNav: React.FC<SideNavProps> = ({
  activeTab,
  tabs,
  onNavigate,
  onLongPressStart,
  onLongPressEnd,
  onTabLongPress,
  collapsed = false,
}) => {
  // Cache icons on first encounter
  tabs.forEach((tab) => {
    if (tab.icon && !iconCache.has(tab.name)) {
      iconCache.set(tab.name, tab.icon);
    }
  });

  const handleTabClick = (tab: SideNavProps["tabs"][0]) => {
    // Navigate by tab name (matches BottomNav)
    onNavigate(tab.name);
  };

  const handleLongPress = (tab: SideNavProps["tabs"][0]) => {
    // Long press by tab name
    onTabLongPress(tab.name);
  };

  const { user: userDetails, logout, isStaff } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleLogout = () => {
    setShowLogoutAlert(true);
  };

  const confirmLogout = async () => {
    setShowLogoutAlert(false);
    try {
      await logout();
      // The AuthContext will handle the redirect after logout
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const cancelLogout = () => {
    setShowLogoutAlert(false);
  };

  return (
    <div className={`side-nav${collapsed ? " side-nav--collapsed" : ""}`}
      aria-hidden={collapsed}>
      <div className="side-nav-header">
        <div className="side-nav-logo">
          <img src={logo} alt="Logo" className="logo-image" />
        </div>
      </div>

      <div className="side-nav-content">
        <nav className="side-nav-menu" style={{paddingBottom:"70px"}}>
          {tabs.map((tab) => (
            <div
              key={tab.name}
              className={`side-nav-item 
                ${activeTab === tab.name ? "active" : ""} 
                ${tab.invert ? "inverted" : ""}`}
              onClick={() => handleTabClick(tab)}
              onMouseDown={() => onLongPressStart(() => handleLongPress(tab))}
              onMouseUp={onLongPressEnd}
              onMouseLeave={onLongPressEnd}
              onTouchStart={() => onLongPressStart(() => handleLongPress(tab))}
              onTouchEnd={onLongPressEnd}
              role="button"
              tabIndex={0}
              aria-current={activeTab === tab.name ? "page" : undefined}
            >
              <div className="side-nav-item-content ion-activatable">
                <div className="side-nav-icon">
                  {(() => {
                    const IconComponent = iconCache.get(tab.name);
                    if (!IconComponent) {
                      return (
                        <img
                          src={tab.icon}
                          alt={tab.name}
                          className={`nav-icon ${tab.invert ? "inverted" : ""}`}
                        />
                      );
                    }
                    const isActive = activeTab === tab.name;
                    return (
                      <IconComponent
                        className={isActive ? "active-icon" : "inactive-icon"}
                        aria-hidden="true"
                        width={isActive ? 32 : 40}
                        height={isActive ? 32 : 40}
                      />
                    );
                  })()}
                </div>
                <span className="side-nav-label">
                  {tab.name.charAt(0).toUpperCase() + tab.name.slice(1)}
                </span>
                <IonIcon
                  icon={chevronForward}
                  className={`side-nav-arrow ${activeTab === tab.name ? "active" : ""
                    }`}
                />
                <IonRippleEffect />
              </div>
            </div>
          ))}
          {/* R-753: provider staff only. Mirrors the portal's "Switch to Patient App". */}
          {isStaff && (
            <div
              className="side-nav-item"
              onClick={() => void goToProviderPortal(portalSwitchUrl())}
              role="button"
              tabIndex={0}
              data-testid="switch-to-provider-portal"
            >
              <div className="side-nav-item-content ion-activatable">
                <div className="side-nav-icon">
                  <IonIcon
                    icon={swapHorizontalOutline}
                    className="inactive-icon"
                    aria-hidden="true"
                    style={{ width: 32, height: 32 }}
                  />
                </div>
                <span className="side-nav-label">{SWITCH_TO_PORTAL_LABEL}</span>
                <IonIcon icon={chevronForward} className="side-nav-arrow" />
                <IonRippleEffect />
              </div>
            </div>
          )}
        </nav>
      </div>

      <div
        className="side-nav-footer"
        style={{
          position: "sticky",
          bottom: 0,
          paddingBottom: "max(12px, env(safe-area-inset-bottom))",
          backgroundColor: "#13b5d5",
          borderRadius: "12px 12px 0px 0px",
        }}
      >
        <div className="side-nav-user" ref={dropdownRef} onClick={toggleDropdown}>
          <div className="user-avatar">
            {userDetails?.avatar ? (
              isCustomAvatar(userDetails.avatar) ? (
                // Plain <img> so the browser handles same-site cookie auth and
                // skips the CORS preflight that the fetch-based AuthenticatedImage
                // hits on fresh login (cookie not yet committed → 401 w/o ACAO).
                <Avatar
                  unstyled
                  initials={getInitials(
                    userDetails?.first_name || "",
                    userDetails?.last_name || ""
                  )}
                  imageSrc={userDetails.avatar}
                  className="w-full h-full"
                  innerClassName="w-full h-full flex items-center justify-center"
                  imageClassName="w-full h-full object-cover"
                />
              ) : (
                <svg className="w-full h-full" viewBox="0 0 200 200">
                  <use href={`${avatarsSprite}#${userDetails.avatar}`} />
                </svg>
              )
            ) : userDetails?.first_name || userDetails?.last_name ? (
              <InitialsAvatar
                firstName={userDetails?.first_name}
                lastName={userDetails?.last_name}
                size={40}
              />
            ) : (
              <IonIcon
                icon={personCircleOutline}
                style={{ width: "40px", height: "40px", color: "#fff" }}
              />
            )}
          </div>

          <div
            style={{
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: "8px",
            }}
          >
            <span style={{ 
              overflow: "hidden", 
              textOverflow: "ellipsis", 
              whiteSpace: getUserName(userDetails)?.length > 10 ? "normal" : "nowrap",
              wordBreak: "break-word"
            }}>
              {getUserName(userDetails) || "User"}
            </span>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleLogout();
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                background: "transparent",
                color: "white",
                padding: 0,
                cursor: "pointer",
              }}
              aria-label="Logout"
              title="Logout"
            >
              <IonIcon icon={logOutOutline} className="logout-icon" />
            </button>
          </div>
        </div>
      </div>

      {showLogoutAlert && (
        <RequestPopup
          onClose={cancelLogout}
          title="Confirm Logout"
          description="Are you sure you want to log out of your account?"
          buttonText="Logout"
          onProceed={confirmLogout}
        />
      )}
    </div>
  );
};

export default SideNav;
